import React, { useCallback, useMemo, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import type { User } from "firebase/auth";
import {
  fetchAllOrganizations,
  fetchContactsForOrganizations,
  updateOrganizationBilling,
  updateOrganizationActive,
  startTrial,
  PLAN_LIMITS,
  type OrganizationRecord,
  type PlanId,
  type PlanLimits,
  type UpdateBillingParams,
} from "../../../api/organizations";
import { OrganizationsTable } from "../OrganizationsTable";
import { SetPlanAndStatusModal } from "../SetPlanAndStatusModal";
import { DeleteOrganizationModal } from "../DeleteOrganizationModal";
import { OrganizationActivityDrawer } from "../OrganizationActivityDrawer";
import {
  PLAN_IDS_WITH_CUSTOM,
  SUBSCRIPTION_STATUSES,
} from "../constants";
import { getReasonToCall, isNeedsCalling, isPlanExpired } from "../utils";
import "../intelligence/IntelligenceGlass.css";
import { IntelligenceManageModal } from "../intelligence";
import { TopClientsModal } from "../TopClientsModal";

interface OrganizationsTabProps {
  records: OrganizationRecord[];
  setRecords: React.Dispatch<React.SetStateAction<OrganizationRecord[]>>;
  loading: boolean;
  error: string | null;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  currentPage: number;
  hasMore: boolean;
  goToPage: (page: number) => void;
  planCatalog: Record<PlanId, PlanLimits>;
  onOpenGlobalPlanModal: () => void;
  onOpenIntelligenceCatalogModal: () => void;
  onOpenIntelligenceShipGateModal: () => void;
  user: User;
}

export const OrganizationsTab: React.FC<OrganizationsTabProps> = ({
  records,
  setRecords,
  loading,
  setError,
  currentPage,
  hasMore,
  goToPage,
  planCatalog,
  onOpenGlobalPlanModal,
  onOpenIntelligenceCatalogModal,
  onOpenIntelligenceShipGateModal,
  user,
}) => {
  const adminBaseUrl = useMemo(() => {
    const configured = process.env.REACT_APP_ADMIN_URL?.trim();
    if (configured) {
      return configured.replace(/\/+$/, "");
    }

    if (
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1")
    ) {
      return "http://localhost:3000";
    }

    return "https://admin.mayorx.net";
  }, []);
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterPlan, setFilterPlan] = useState<string>("");
  const [filterIntelligence, setFilterIntelligence] = useState<string>("");
  const [filterPlanExpired, setFilterPlanExpired] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editModal, setEditModal] = useState<{ org: OrganizationRecord } | null>(null);
  const [deleteModal, setDeleteModal] = useState<{ org: OrganizationRecord } | null>(
    null,
  );
  const [intelligenceModal, setIntelligenceModal] = useState<{
    org: OrganizationRecord;
  } | null>(null);
  const [topClientsModal, setTopClientsModal] = useState<{
    org: OrganizationRecord;
  } | null>(null);
  const [activityDrawer, setActivityDrawer] = useState<{
    org: OrganizationRecord;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [actionBusyId, setActionBusyId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const planExpiredCount = useMemo(
    () => records.filter(isPlanExpired).length,
    [records],
  );

  const filtered = useMemo(() => {
    let list = [...records];
    if (filterStatus) {
      list = list.filter((r) => r.subscriptionStatus === filterStatus);
    }
    if (filterPlan) {
      list = list.filter((r) => r.planId === filterPlan);
    }
    if (filterIntelligence === "none") {
      list = list.filter((r) => !r.intelligenceCapacityId);
    } else if (filterIntelligence === "trial") {
      list = list.filter((r) => Boolean(r.intelligenceTrialing));
    } else if (filterIntelligence === "active") {
      list = list.filter(
        (r) => Boolean(r.intelligenceCapacityId) && !r.intelligenceTrialing,
      );
    } else if (
      filterIntelligence === "lite" ||
      filterIntelligence === "plus" ||
      filterIntelligence === "extra"
    ) {
      list = list.filter((r) => r.intelligenceCapacityId === filterIntelligence);
    }
    if (filterPlanExpired) {
      list = list.filter(isPlanExpired);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(term) ||
          (r.email && r.email.toLowerCase().includes(term)) ||
          r.id.toLowerCase().includes(term),
      );
    }
    return list;
  }, [
    records,
    filterStatus,
    filterPlan,
    filterIntelligence,
    filterPlanExpired,
    searchTerm,
  ]);

  const handleSaveEdit = async (orgId: string, updates: UpdateBillingParams) => {
    setSaving(true);
    setError(null);
    try {
      await updateOrganizationBilling(orgId, updates);
      const org = editModal?.org;
      if (!org || org.id !== orgId) return;
      const plan = updates.customPlan ?? null;
      const nextPlan: PlanLimits | null = plan && "name" in plan && plan.name ? (plan as PlanLimits) : null;
      setRecords((prev) =>
        prev.map((o) => {
          if (o.id !== orgId) return o;
          return {
            ...o,
            subscriptionStatus: updates.subscriptionStatus ?? o.subscriptionStatus,
            planId: updates.planId ?? o.planId,
            plan: nextPlan ?? o.plan,
            stripeCustomerId: updates.stripeCustomerId ?? o.stripeCustomerId,
            stripeSubscriptionId: updates.stripeSubscriptionId ?? o.stripeSubscriptionId,
            stripePriceId: updates.stripePriceId ?? o.stripePriceId,
            selectablePlanIds: updates.selectablePlanIds ?? o.selectablePlanIds,
            currentPeriodEnd: updates.currentPeriodEnd ? new Date(updates.currentPeriodEnd as Date) : o.currentPeriodEnd,
            updated: new Date(),
          };
        }),
      );
      setEditModal(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  const handleStartTrial = async (org: OrganizationRecord) => {
    setActionBusyId(org.id);
    setError(null);
    try {
      await startTrial(org.id);
      const periodEnd = new Date();
      periodEnd.setDate(periodEnd.getDate() + 30);
      setRecords((prev) =>
        prev.map((item) =>
          item.id === org.id
            ? {
                ...item,
                subscriptionStatus: "trialing",
                planId: "micro",
                plan: planCatalog.micro ?? PLAN_LIMITS.micro,
                currentPeriodEnd: periodEnd,
                hasUsedFreeTrial: true,
                updated: new Date(),
              }
            : item,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to start trial");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleDeactivate = async (org: OrganizationRecord) => {
    if (
      !window.confirm(
        `Deactivate plan for "${org.name}"? Status will be set to canceled.`,
      )
    )
      return;
    setActionBusyId(org.id);
    setError(null);
    try {
      await updateOrganizationBilling(org.id, { subscriptionStatus: "canceled" });
      setRecords((prev) =>
        prev.map((item) =>
          item.id === org.id
            ? { ...item, subscriptionStatus: "canceled", updated: new Date() }
            : item,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to deactivate");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleToggleActive = async (org: OrganizationRecord) => {
    setActionBusyId(org.id);
    setError(null);
    try {
      await updateOrganizationActive(org.id, !org.active);
      setRecords((prev) =>
        prev.map((item) =>
          item.id === org.id
            ? { ...item, active: !org.active, updated: new Date() }
            : item,
        ),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update");
    } finally {
      setActionBusyId(null);
    }
  };

  const handleExportNeedsCalling = async () => {
    setExporting(true);
    setError(null);
    try {
      const allOrgs = await fetchAllOrganizations(100);
      const orgs = allOrgs.filter(isNeedsCalling);
      if (orgs.length === 0) {
        setError("No organizations need calling to export.");
        setExporting(false);
        return;
      }
      const contactsMap = await fetchContactsForOrganizations(orgs.map((o) => o.id));
      const escapeCsv = (s: string) => {
        const t = String(s).replace(/"/g, '""');
        return t.includes(",") || t.includes('"') || t.includes("\n")
          ? `"${t}"`
          : t;
      };
      const rows: string[][] = [
        [
          "Organization name",
          "Contact name",
          "Contact last name",
          "Phone",
          "Email",
          "Reason to call",
        ],
      ];
      for (const org of orgs) {
        const contacts = contactsMap.get(org.id) ?? [];
        const first = contacts[0];
        const displayName = first?.displayName ?? "";
        const parts = displayName.trim().split(/\s+/);
        const firstName = parts[0] ?? "";
        const lastName = parts.length > 1 ? parts.slice(1).join(" ") : "";
        const phone = first?.phone || org.phone || "";
        const email = first?.email || org.email || "";
        const reason = getReasonToCall(org);
        rows.push([org.name, firstName, lastName, phone, email, reason]);
      }
      const csv = rows.map((row) => row.map(escapeCsv).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `needs-calling-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const handleOpenInAdmin = useCallback(
    (org: OrganizationRecord) => {
      if (typeof window === "undefined") return;
      const url = new URL(adminBaseUrl);
      url.searchParams.set("organizationId", org.id);
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    },
    [adminBaseUrl],
  );

  return (
    <section className="org-section-card text-dark">
      <header className="org-section-card__header">
        <h2 className="org-section-card__title">All organizations</h2>
        <p className="org-section-card__subtitle">
          Filter, search, and manage subscription status and plans. Click a row
          to open the activity baseline drawer.
        </p>
        <div className="org-toolbar mt-3">
          <div className="org-toolbar__row org-toolbar__row--actions">
            <Button variant="outline-secondary" onClick={onOpenGlobalPlanModal}>
              Global plan catalog
            </Button>
            <div
              className="org-toolbar__intel"
              role="group"
              aria-label="Intelligence settings"
            >
              <span className="org-toolbar__intel-label">Intelligence</span>
              <div className="btn-group org-toolbar__intel-actions">
                <Button
                  variant="outline-secondary"
                  onClick={onOpenIntelligenceCatalogModal}
                >
                  Catalog
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={onOpenIntelligenceShipGateModal}
                >
                  Availability
                </Button>
              </div>
            </div>
            <Button
              variant={filterPlanExpired ? "danger" : "outline-danger"}
              onClick={() => setFilterPlanExpired((prev) => !prev)}
              aria-pressed={filterPlanExpired}
              title="Show organizations with expired plans"
            >
              Plan expired{" "}
              {filterPlanExpired
                ? `(${filtered.length})`
                : `(${planExpiredCount})`}
            </Button>
            <Button
              variant="outline-success"
              onClick={handleExportNeedsCalling}
              disabled={exporting}
            >
              {exporting ? "Exporting…" : "Export to Excel"}
            </Button>
          </div>
          <div className="org-toolbar__row org-toolbar__row--filters">
            <Form.Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              aria-label="Filter by status"
            >
              <option value="">All statuses</option>
              {SUBSCRIPTION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value)}
              aria-label="Filter by plan"
            >
              <option value="">All plans</option>
              {PLAN_IDS_WITH_CUSTOM.map((p) => (
                <option key={p} value={p}>
                  {(planCatalog[p] ?? PLAN_LIMITS[p])?.name ?? p}
                </option>
              ))}
            </Form.Select>
            <Form.Select
              value={filterIntelligence}
              onChange={(e) => setFilterIntelligence(e.target.value)}
              aria-label="Filter by Intelligence"
            >
              <option value="">All Intelligence</option>
              <option value="active">Active (paid/grant)</option>
              <option value="trial">In trial</option>
              <option value="none">None</option>
              <option value="lite">Lite</option>
              <option value="plus">Plus</option>
              <option value="extra">Extra</option>
            </Form.Select>
            <Form.Control
              className="org-toolbar__search"
              type="text"
              placeholder="Search name, email, id..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label="Search organizations"
            />
          </div>
          <p className="org-toolbar__meta mb-0">
            Page {currentPage}
            {records.length > 0 && ` · ${filtered.length} shown`}
          </p>
        </div>
      </header>
      <div className="org-section-card__body">
        {loading ? (
          <div className="d-flex align-items-center gap-2 text-muted">
            <Spinner animation="border" size="sm" />
            <span>Loading organizations...</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-muted small">
            {records.length === 0
              ? "No organizations found."
              : "No organizations match the current filters."}
          </div>
        ) : (
          <OrganizationsTable
            organizations={filtered}
            planCatalog={planCatalog}
            loading={loading}
            currentPage={currentPage}
            hasMore={hasMore}
            actionBusyId={actionBusyId}
            onSetPlan={(org) => setEditModal({ org })}
            onStartTrial={handleStartTrial}
            onDeactivate={handleDeactivate}
            onToggleActive={handleToggleActive}
            onOpenInAdmin={handleOpenInAdmin}
            onDeleteOrganization={(org) => setDeleteModal({ org })}
            onManageIntelligence={(org) => setIntelligenceModal({ org })}
            onViewTopClients={(org) => setTopClientsModal({ org })}
            onOpenActivity={(org) => setActivityDrawer({ org })}
            onPrevPage={() => goToPage(currentPage - 1)}
            onNextPage={() => goToPage(currentPage + 1)}
          />
        )}
      </div>

      <OrganizationActivityDrawer
        show={!!activityDrawer}
        onHide={() => setActivityDrawer(null)}
        org={activityDrawer?.org ?? null}
        user={user}
      />

      <SetPlanAndStatusModal
        show={!!editModal}
        onHide={() => setEditModal(null)}
        org={editModal?.org ?? null}
        planCatalog={planCatalog}
        saving={saving}
        user={user}
        onSave={handleSaveEdit}
      />

      <IntelligenceManageModal
        show={!!intelligenceModal}
        onHide={() => setIntelligenceModal(null)}
        org={intelligenceModal?.org ?? null}
        user={user}
        onUpdated={(patch) => {
          const orgId = intelligenceModal?.org.id;
          if (!orgId) return;
          setRecords((prev) =>
            prev.map((o) =>
              o.id === orgId ? { ...o, ...patch, updated: new Date() } : o,
            ),
          );
          setIntelligenceModal((prev) =>
            prev ? { org: { ...prev.org, ...patch } } : prev,
          );
        }}
      />

      <TopClientsModal
        show={!!topClientsModal}
        onHide={() => setTopClientsModal(null)}
        org={topClientsModal?.org ?? null}
        user={user}
      />

      <DeleteOrganizationModal
        show={!!deleteModal}
        onHide={() => setDeleteModal(null)}
        org={deleteModal?.org ?? null}
        user={user}
        onDeleted={(organizationId, deletedLinkedSandboxId) => {
          setRecords((prev) =>
            prev.filter(
              (o) =>
                o.id !== organizationId && o.id !== deletedLinkedSandboxId,
            ),
          );
          setDeleteModal(null);
        }}
      />
    </section>
  );
}
